import { getCountryAndLanguage } from '~/utils/getCountryAndLanguages';
import type { CountryLanguages } from '~/types/countryLanguages';

const useCountryLanguagesState = () =>
    useState<{ published: CountryLanguages | null; draft: CountryLanguages | null }>('countryLanguages', () => ({
        published: null,
        draft: null
    }));

/**
 * Do not use this inside API routes. Use `getInitializedCountryLanguagesApi()` instead.
 */
export async function initCountryLanguages(draftModeCookie: string | false = false): Promise<void> {
    const state = useCountryLanguagesState();

    /**
     * Without a draft cookie, `getCountryAndLanguage(draftModeCookie)` and
     * `getCountryAndLanguage(false)` are the same call with the same arguments,
     * so filling both slots separately meant every regular page render issued
     * two identical requests to the Storyblok proxy. Share the one response
     * instead; nothing mutates the returned config.
     */
    if (!draftModeCookie) {
        state.value.published ??= await getCountryAndLanguage(false);
        state.value.draft ??= state.value.published;
        return;
    }

    state.value.draft ??= await getCountryAndLanguage(draftModeCookie);
    state.value.published ??= await getCountryAndLanguage(false);
}

export function useCountryLanguages(): CountryLanguages {
    const draftMode = useDraftMode();
    const mode = draftMode.isDraftModeEnabled.value ? StoryblokVersion.Draft : StoryblokVersion.Published;
    return getCountryLanguages(mode);
}

/**
 * Do not use this inside API routes. Use `getInitializedCountryLanguagesApi()` instead.
 */
export const getCountryLanguages = (mode: StoryblokVersion = StoryblokVersion.Published): CountryLanguages => {
    const state = useCountryLanguagesState();
    if (!state.value[mode]) {
        throw new Error(
            'countryLanguages config not initialized - initCountryLanguages() should be called at app entry'
        );
    }

    return state.value[mode];
};

export const getDefaultLanguageForCountry = (
    countryCode: string,
    mode: StoryblokVersion = StoryblokVersion.Published
): string | undefined => {
    const countryLanguages = getCountryLanguages(mode);
    return countryLanguages[countryCode]?.availableLanguages?.[0];
};
